import React, { useState, useEffect } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import './RecipeDetail.css';
import Loading from '../../common/Loading';

function RecipeDetail() {
  const { recipeName } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    fetch(`https://api.mutantbutton2.com/public/recipes/${recipeName}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Recipe not found');
        }
        return response.json();
      })
      .then(data => {
        setRecipe(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching recipe:', error);
        setError(error.message);
        setLoading(false);
      });
  }, [recipeName]);

  if (loading) {
    return <div className="recipe-detail"><Loading /></div>;
  }

  if (error) {
    return <div className="recipe-detail error">Error: {error}</div>;
  }

  if (!recipe) {
    return <div className="recipe-detail">Recipe not found</div>;
  }

  const formattedName = recipe.name
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return (
    <div className="recipe-detail">
      <nav className="breadcrumb">
        <NavLink to="/">Home</NavLink>
        <span className="separator">{'>'}</span>
        <NavLink to="/recipes">Recipes</NavLink>
        <span className="separator">{'>'}</span>
        <span className="current">{formattedName}</span>
      </nav>

      <div className="recipe-header">
        <h1>{formattedName}</h1>
        {recipe.servings && (
          <span className="recipe-servings">Serves {recipe.servings}</span>
        )}
      </div>

      {recipe.nutrients && recipe.nutrients.calories && (
        <section className="recipe-section">
          <h2>Nutrition</h2>
          <div className="nutrients-info">
            {recipe.nutrients.calories.total_kcal && (
              <div className="nutrient-item">
                <span className="nutrient-label">Total Calories:</span>
                <span className="nutrient-value">{recipe.nutrients.calories.total_kcal} kcal</span>
              </div>
            )}
            {recipe.nutrients.calories.per_serving_kcal && (
              <div className="nutrient-item">
                <span className="nutrient-label">Per Serving:</span>
                <span className="nutrient-value">{recipe.nutrients.calories.per_serving_kcal} kcal</span>
              </div>
            )}
            {recipe.nutrients.calories.note && (
              <div className="nutrient-note">{recipe.nutrients.calories.note}</div>
            )}
          </div>
        </section>
      )}
      
      <section className="recipe-section">
        <h2>Ingredients</h2>
        {recipe.ingredients && (() => {
          const regularIngredients = recipe.ingredients.filter(ing => !ing.marinate || Object.keys(ing.marinate).length === 0);
          const marinatedIngredients = recipe.ingredients.filter(ing => ing.marinate && Object.keys(ing.marinate).length > 0);
          
          // Group regular ingredients by group property
          const groupedRegular = regularIngredients.reduce((acc, ingredient) => {
            const group = ingredient.group || 'Main';
            if (!acc[group]) {
              acc[group] = [];
            }
            acc[group].push(ingredient);
            return acc;
          }, {});
          
          // Group marinated ingredients by group property
          const groupedMarinated = marinatedIngredients.reduce((acc, ingredient) => {
            const group = ingredient.group || 'Marinade';
            if (!acc[group]) {
              acc[group] = [];
            }
            acc[group].push(ingredient);
            return acc;
          }, {});
          
          return (
            <>
              {Object.keys(groupedRegular).length > 0 && (
                <>
                  {Object.entries(groupedRegular).map(([groupName, ingredients]) => (
                    <div key={groupName} className="ingredient-group-section">
                      <h3>{groupName}</h3>
                      <ul className="ingredients-list">
                        {ingredients.map((ingredient, index) => (
                          <li key={index}>
                            <strong>{ingredient.name}</strong>
                            {ingredient.quantity && ` - ${ingredient.quantity}`}
                            {ingredient.unit && ` ${ingredient.unit}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </>
              )}
              {Object.keys(groupedMarinated).length > 0 && (
                <>
                  {Object.entries(groupedMarinated).map(([groupName, ingredients]) => (
                    <div key={groupName} className="ingredient-group-section marinade-subsection">
                      <h3>{groupName}</h3>
                      <ul className="ingredients-list marinade-list">
                        {ingredients.map((ingredient, index) => (
                          <li key={index}>
                            <strong>{ingredient.name}</strong>
                            {ingredient.quantity && ` - ${ingredient.quantity}`}
                            {ingredient.unit && ` ${ingredient.unit}`}
                            {ingredient.marinate && Object.keys(ingredient.marinate).length > 0 && (
                              <div className="ingredient-marinate">
                                {Object.entries(ingredient.marinate).map(([key, value]) => (
                                  <span key={key} className="marinate-item">
                                    {key}: {typeof value === 'object' ? JSON.stringify(value) : value}
                                  </span>
                                ))}
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </>
              )}
            </>
          );
        })()}
      </section>

      <section className="recipe-section">
        <h2>Instructions</h2>
        <ol className="instructions-list">
          {recipe.instructions && recipe.instructions.map((instruction, index) => (
            <li key={index}>{instruction.description}</li>
          ))}
        </ol>
      </section>

      {recipe.notes && recipe.notes.length > 0 && (
        <section className="recipe-section">
          <h2>Notes</h2>
          <ul className="notes-list">
            {recipe.notes.map((note, index) => (
              <li key={index}>
                <span className="note-label">Note:</span> {note}
              </li>
            ))}
          </ul>
        </section>
      )}

      {recipe.last_updated && (
        <div className="recipe-updated">
          Last updated: {new Date(recipe.last_updated).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>
      )}
    </div>
  );
}

export default RecipeDetail;
